import React, { useState, useEffect } from 'react';
import { Calculator, Users, Eye, MessageCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const SocialMediaInteractionRateCalculator = () => {
  const [totalImpressions, setTotalImpressions] = useState(100000);
  const [totalInteractions, setTotalInteractions] = useState(5000);
  const [interactionRate, setInteractionRate] = useState(0);

  useEffect(() => {
    calculateInteractionRate();
  }, [totalImpressions, totalInteractions]);

  const calculateInteractionRate = () => {
    const rate = (totalInteractions / totalImpressions) * 100;
    setInteractionRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/social-media-interaction-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Social Media Interaction Rate Calculator: Measure Content Impact | Free Tool</title>
        <meta name="description" content="Calculate your Social Media Interaction Rate instantly. Track audience interactions, compare with platform benchmarks, and optimize your content strategy." />
        <meta name="keywords" content="social media interaction calculator, interaction rate metrics, social media analytics, content impact, audience engagement" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Social Media Interaction Calculator: Boost Content Impact" />
        <meta property="og:description" content="Calculate your Social Media Interaction Rate instantly. Get platform-specific benchmarks and strategies to improve audience interaction." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Social Media Interaction Calculator: Boost Content Impact" />
        <meta property="twitter:description" content="Calculate your Social Media Interaction Rate instantly. Get platform-specific benchmarks and strategies to improve audience interaction." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Social Media Interaction Rate Calculator",
            "description": "Free calculator to measure and optimize social media interaction rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Interaction Rate",
              "Platform-specific Benchmarks",
              "Interaction Optimization Tips",
              "Performance Tracking",
              "Content Strategy Insights"
            ],
            "about": {
              "@type": "Thing",
              "name": "Social Media Interaction Rate",
              "description": "The percentage of impressions that resulted in interactions such as likes, comments, shares, clicks, and other engagement actions."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Social Media Managers, Content Creators, Digital Marketers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Social Media Interaction Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total number of impressions"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input total number of interactions"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your interaction rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Social Media Interaction Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Impressions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Eye className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalImpressions}
                  onChange={(e) => setTotalImpressions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Interactions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MessageCircle className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalInteractions}
                  onChange={(e) => setTotalInteractions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2">
            {interactionRate}%
          </div>
          <p className="text-gray-600">Interaction Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Platform Benchmarks</h2>
        <p className="text-gray-600">
          Your social media interaction rate is {interactionRate}%. This means that {interactionRate}% of the impressions on your content resulted in some form of interaction (likes, comments, shares, clicks, etc.).
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Platform-Specific Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Instagram Posts: 1-3% (average), {'>'} 3% (excellent)</li>
          <li>Instagram Stories: 5-7% (average), {'>'} 7% (excellent)</li>
          <li>Facebook Posts: 0.5-1% (average), {'>'} 1% (excellent)</li>
          <li>Twitter: 0.2-0.5% (average), {'>'} 0.5% (excellent)</li>
          <li>LinkedIn: 2-3% (average), {'>'} 3% (excellent)</li>
          <li>TikTok: 5-8% (average), {'>'} 8% (excellent)</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Interaction Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Create engaging and interactive content (polls, quizzes, questions)</li>
          <li>Use strong calls-to-action in your posts</li>
          <li>Experiment with different content formats</li>
          <li>Engage with your audience promptly</li>
          <li>Post at optimal times for your audience</li>
          <li>Use relevant hashtags strategically</li>
          <li>Collaborate with influencers or brands</li>
          <li>Create platform-specific content</li>
          <li>Use interactive stickers and features</li>
          <li>Encourage user-generated content</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Content Performance Insights</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Interactive posts get 2x more interactions than static posts</li>
          <li>Questions in captions increase comments by 50%</li>
          <li>Videos receive 38% more interactions than images</li>
          <li>User-generated content gets 28% higher interaction rates</li>
          <li>First hour of posting accounts for 40% of daily interactions</li>
        </ul>
      </div>
    </div>
  );
};

export default SocialMediaInteractionRateCalculator;
